import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { formatDistanceToNow } from "date-fns";
import {
  Briefcase,
  ChevronDown,
  History,
  LifeBuoy,
  Lightbulb,
  Loader2,
  Lock,
  MessageSquare,
  Send,
  Sparkles,
  Square,
  SquarePen,
  Trash2,
  User,
  X,
} from "lucide-react";
import MessageList from "./MessageList";
import ActivityTab from "./ActivityTab";
import type { AssistantChat } from "./useAssistantChat";
import type { AssistantEntity, AssistantMode } from "./types";
import { entityLabel } from "./pageContext";

const MODE_COPY: Record<AssistantMode, string> = {
  manual: "You approve every action before it happens.",
  auto: "Actions run right away. Payments, trust transactions, cancellations, deletions, and messages to more than 10 people still ask first.",
};

interface Props {
  chat: AssistantChat;
  entity: AssistantEntity | null;
  onDropEntity: () => void;
  suggestions: string[];
  onSend: (text: string) => void;
  onClose: () => void;
  onOpenSupport: () => void;
  supportUnread: number;
  canSeeEveryone: boolean;
  canManageBilling: boolean;
}

const iconBtn =
  "inline-flex h-11 w-11 sm:h-9 sm:w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600";

const AssistantPanel: React.FC<Props> = ({
  chat,
  entity,
  onDropEntity,
  suggestions,
  onSend,
  onClose,
  onOpenSupport,
  supportUnread,
  canSeeEveryone,
  canManageBilling,
}) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"chat" | "activity">("chat");
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const input = chat.draft;
  const setInput = chat.setDraft;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const { refreshThreads, refreshUsage, upgradeRequired } = chat;

  // Load threads and usage on open (skip if we already know the plan does not include it)
  useEffect(() => {
    if (upgradeRequired) return;
    refreshThreads();
    refreshUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus the input when the panel opens
  useEffect(() => {
    const t = setTimeout(() => textareaRef.current?.focus(), 120);
    return () => clearTimeout(t);
  }, []);

  // Return focus to the input when a turn finishes
  const wasStreaming = useRef(false);
  useEffect(() => {
    if (wasStreaming.current && !chat.streaming) textareaRef.current?.focus();
    wasStreaming.current = chat.streaming;
  }, [chat.streaming]);

  // Autosize textarea
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [input]);

  // Close thread menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setConfirmDelete(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  const submit = (text?: string) => {
    const value = (text ?? input).trim();
    if (!value || chat.streaming) return;
    onSend(value);
    if (text === undefined) setInput("");
    setTab("chat");
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  const title = chat.threadTitle || (chat.threadId ? "Conversation" : "New chat");
  const EntityIcon = entity?.type === "matter" ? Briefcase : User;

  // ── Upgrade empty state ──
  const upgradeState = (
    <div className="flex flex-1 flex-col items-center justify-center px-8 py-12 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-emerald-50">
        <Lock className="h-7 w-7 text-[#0F5132]" />
      </div>
      <h3 className="text-base font-bold text-slate-800">The assistant is part of the Elite plan</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-500">
        Look up anything in your firm, log time, draft invoices, text clients and schedule meetings by
        asking in plain language.
      </p>
      {canManageBilling ? (
        <button
          type="button"
          onClick={() => navigate("/settings/subscription")}
          className="mt-5 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-[#0F5132] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#146C43]"
        >
          <Sparkles className="h-4 w-4" />
          View plans
        </button>
      ) : (
        <p className="mt-5 rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Ask your firm administrator to upgrade to Elite.
        </p>
      )}
    </div>
  );

  // ── Empty chat state ──
  const emptyState = (
    <div className="flex flex-col px-5 pb-4 pt-8">
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50">
        <Sparkles className="h-5 w-5 text-[#0F5132]" />
      </div>
      <h3 className="text-base font-bold text-slate-800">How can I help?</h3>
      <p className="mt-1 text-sm leading-relaxed text-slate-500">
        Ask about clients, matters, tasks, billing and your calendar, or have me log time, create tasks,
        draft invoices and message clients. {chat.mode === "manual" ? "You approve every action first." : ""}
      </p>
      <div className="mt-5 space-y-2">
        {suggestions.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => submit(s)}
            className="flex min-h-[44px] w-full items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-left text-sm text-slate-700 transition-colors hover:border-emerald-300 hover:bg-emerald-50/50"
          >
            <Lightbulb className="h-4 w-4 shrink-0 text-[#198754]" />
            {s}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* ── Header ── */}
      <div className="shrink-0 border-b border-slate-200 bg-white px-3 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))] sm:pt-2">
        <div className="flex items-center gap-1">
          <div ref={menuRef} className="relative min-w-0 flex-1">
            <button
              type="button"
              onClick={() => {
                setMenuOpen((v) => !v);
                if (!menuOpen) refreshThreads();
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape" && menuOpen) {
                  e.preventDefault();
                  setMenuOpen(false);
                }
              }}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              className="flex min-h-[44px] max-w-full items-center gap-1.5 rounded-lg px-2 text-left hover:bg-slate-100 sm:min-h-[36px]"
            >
              <Sparkles className="h-4 w-4 shrink-0 text-[#0F5132]" />
              <span className="truncate text-sm font-bold text-slate-800">{title}</span>
              <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.12 }}
                  role="menu"
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.preventDefault();
                      setMenuOpen(false);
                    }
                  }}
                  className="absolute left-0 top-full z-10 mt-1 w-[min(340px,calc(100vw-2rem))] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
                >
                  <p className="border-b border-slate-100 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    Recent conversations
                  </p>
                  <div className="max-h-72 overflow-y-auto py-1">
                    {chat.threads.length === 0 ? (
                      <p className="px-3 py-4 text-center text-xs text-slate-500">
                        {chat.threadsLoaded ? "No conversations yet." : "Loading..."}
                      </p>
                    ) : (
                      chat.threads.map((t) => {
                        let rel = "";
                        try {
                          rel = t.updatedAt ? formatDistanceToNow(new Date(t.updatedAt), { addSuffix: true }) : "";
                        } catch {}
                        const active = t._id === chat.threadId;
                        return (
                          <div
                            key={t._id}
                            className={`group flex items-center gap-1 px-1 ${active ? "bg-emerald-50/60" : ""}`}
                          >
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setMenuOpen(false);
                                setTab("chat");
                                if (!active) chat.openThread(t._id);
                              }}
                              className="flex min-h-[44px] min-w-0 flex-1 flex-col justify-center rounded-lg px-2 py-1.5 text-left hover:bg-slate-50"
                            >
                              <span className="truncate text-sm font-medium text-slate-800">
                                {t.title || "Untitled conversation"}
                              </span>
                              {rel && <span className="text-[11px] text-slate-400">{rel}</span>}
                            </button>
                            {confirmDelete === t._id ? (
                              <button
                                type="button"
                                onClick={() => {
                                  chat.deleteThread(t._id);
                                  setConfirmDelete(null);
                                }}
                                className="min-h-[36px] shrink-0 rounded-lg px-2 text-xs font-semibold text-red-600 hover:bg-red-50"
                              >
                                Delete
                              </button>
                            ) : (
                              <button
                                type="button"
                                aria-label={`Delete ${t.title || "conversation"}`}
                                onClick={() => setConfirmDelete(t._id)}
                                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-slate-300 hover:bg-red-50 hover:text-red-500 sm:h-8 sm:w-8 sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            type="button"
            aria-label="New chat"
            title="New chat"
            onClick={() => {
              chat.newChat();
              setTab("chat");
              setInput("");
              requestAnimationFrame(() => textareaRef.current?.focus());
            }}
            className={iconBtn}
          >
            <SquarePen className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label={supportUnread > 0 ? `Contact support, ${supportUnread} unread` : "Contact support"}
            title="Contact Kavoa support"
            onClick={onOpenSupport}
            className={`${iconBtn} relative`}
          >
            <LifeBuoy className="h-4 w-4" />
            {supportUnread > 0 && (
              <span className="absolute right-1 top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-white">
                {supportUnread > 9 ? "9+" : supportUnread}
              </span>
            )}
          </button>
          <button type="button" aria-label="Close assistant" onClick={onClose} className={iconBtn}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {!chat.upgradeRequired && (
          <>
            <div className="mt-1.5 flex items-center justify-between gap-2 px-1">
              <div role="tablist" aria-label="Assistant views" className="flex gap-1">
                {(
                  [
                    { id: "chat", label: "Chat", Icon: MessageSquare },
                    { id: "activity", label: "Activity", Icon: History },
                  ] as const
                ).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.id}
                    onClick={() => setTab(t.id)}
                    className={`relative inline-flex min-h-[40px] items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold transition-colors sm:min-h-[32px] ${
                      tab === t.id ? "text-[#0F5132]" : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {tab === t.id && (
                      <motion.span
                        layoutId="assistant-tab-pill"
                        className="absolute inset-0 rounded-lg bg-emerald-50"
                        transition={{ type: "spring", stiffness: 400, damping: 32 }}
                      />
                    )}
                    <t.Icon className="relative h-3.5 w-3.5" />
                    <span className="relative">{t.label}</span>
                  </button>
                ))}
              </div>
              <div role="radiogroup" aria-label="Approval mode" className="flex rounded-lg bg-slate-100 p-0.5 text-xs font-semibold">
                {(["manual", "auto"] as AssistantMode[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="radio"
                    aria-checked={chat.mode === m}
                    onClick={() => chat.setMode(m)}
                    className={`min-h-[36px] rounded-md px-3 transition-colors sm:min-h-[28px] ${
                      chat.mode === m ? "bg-white text-[#0F5132] shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {m === "manual" ? "Manual" : "Auto"}
                  </button>
                ))}
              </div>
            </div>
            <p className="mt-1.5 px-1 text-[11px] leading-snug text-slate-500">{MODE_COPY[chat.mode]}</p>
          </>
        )}
      </div>

      {/* ── Body ── */}
      {chat.upgradeRequired ? (
        upgradeState
      ) : tab === "activity" ? (
        <div className="min-h-0 flex-1">
          <ActivityTab
            canSeeEveryone={canSeeEveryone}
            onUpgradeRequired={chat.markUpgradeRequired}
            onOpenThread={(id) => {
              setTab("chat");
              if (id !== chat.threadId) chat.openThread(id);
            }}
          />
        </div>
      ) : (
        <>
          <div className="min-h-0 flex-1 overflow-y-auto bg-[#F8FAF9]">
            {chat.loadingThread ? (
              <div className="flex h-40 items-center justify-center text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : chat.items.length === 0 ? (
              emptyState
            ) : (
              <MessageList
                items={chat.items}
                streaming={chat.streaming}
                onApprove={chat.approve}
                onSkip={chat.skip}
              />
            )}
          </div>

          {/* ── Composer ── */}
          <div className="shrink-0 border-t border-slate-200 bg-white px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2.5 sm:pb-3">
            {entity && (
              <div className="mb-2 flex">
                <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 py-0.5 pl-2.5 pr-0.5 text-xs font-medium text-[#0F5132]">
                  <EntityIcon className="h-3 w-3 shrink-0" />
                  <span className="truncate">On: {entity.name || entityLabel(entity.type)}</span>
                  <button
                    type="button"
                    aria-label="Do not use this page as context"
                    onClick={onDropEntity}
                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full hover:bg-emerald-100"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              </div>
            )}
            <div className="flex items-end gap-2">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                disabled={chat.streaming}
                placeholder={chat.streaming ? "Working on it..." : "Ask or tell Kavoa to do something"}
                aria-label="Message the assistant"
                className="max-h-40 min-h-[44px] flex-1 resize-none rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-base text-slate-800 placeholder:text-slate-400 focus:border-emerald-600 focus:outline-none disabled:bg-slate-50 sm:text-sm"
              />
              {chat.streaming ? (
                <button
                  type="button"
                  onClick={chat.stop}
                  aria-label="Stop"
                  className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                >
                  <Square className="h-3.5 w-3.5 fill-current" />
                  Stop
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => submit()}
                  disabled={!input.trim()}
                  aria-label="Send"
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#0F5132] text-white transition-colors hover:bg-[#146C43] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Send className="h-4 w-4" />
                </button>
              )}
            </div>
            {chat.usage && chat.usage.limit != null && (
              <p className="mt-1.5 text-center text-[11px] text-slate-400">
                {chat.usage.used} of {chat.usage.limit} requests used this month
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AssistantPanel;
